import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { createPadEntry, updatePadEntry } from "./api";
import {
  PAD_CATEGORIES,
  ALTERNATE_METHODS,
  STOP_REASONS,
  LINE_ITEM_KEYS,
  categoryMeta,
  lineItemsFromItem,
  sumLineItems,
  money,
  monthOf,
} from "./padInfoTypes";

// Visual tokens — mirror the addendum modal so the two sections read as one product.
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};
const T = { title: 17, section: 14.5, body: 13.5, label: 12, hint: 11.5 };

const styles = {
  body: { padding: "14px 20px 12px", maxHeight: "74vh", overflowY: "auto", background: C.soft },
  label: { fontSize: T.label, fontWeight: 600, color: C.muted, marginBottom: 5, display: "block" },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 8, height: 38,
    border: `1px solid ${C.inputBorder}`, borderRadius: 8, padding: "0 10px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: T.body, color: C.text, height: "100%", fontFamily: "inherit", minWidth: 0,
  },
  card: { border: `1px solid ${C.border}`, borderRadius: 12, background: "#fff", boxShadow: "0 1px 2px rgba(15,31,53,0.04)", overflow: "hidden" },
  cardHead: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: "#FBFCFE" },
  cardBody: { padding: 14 },
  hint: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.45 },
  sectionTitle: { fontSize: T.section, fontWeight: 700, color: C.text, lineHeight: 1.3 },
};

const Field = ({ label, children, hint, style }) => (
  <div style={style}>
    <label style={styles.label}>{label}</label>
    {children}
    {hint ? <div style={{ ...styles.hint, marginTop: 4 }}>{hint}</div> : null}
  </div>
);

const Box = ({ children, className = "", style }) => (
  <div className={`pim-focusable ${className}`} style={{ ...styles.fieldBox, ...style }}>
    {children}
  </div>
);

const emptyForm = (item, tenantNames = "") => ({
  tenant_names: tenantNames,
  category: "",
  effective_date: "",
  pull_day: 1,
  line_items: lineItemsFromItem(item),
  amount: "",
  amountTouched: false,
  bank: { institution: "", account_holder: "", account_last4: "" },
  delay: { month: "" },
  temp_stop: { alternate_method: "", resume_date: "" },
  stop: { reason: "", reason_text: "", move_out_date: "", alternate_method: "" },
  renewal: { lease_end_date: "" },
  notes: "",
});

const fromExisting = (e) => ({
  tenant_names: e.tenant_names || "",
  category: e.category || "",
  effective_date: e.effective_date || "",
  pull_day: e.pull_day || 1,
  line_items: Array.isArray(e.line_items) && e.line_items.length ? e.line_items.map((li) => ({ ...li })) : [{ key: "basicRent", label: "Rent", amount: "" }],
  amount: e.amount ?? "",
  amountTouched: true,
  bank: { institution: "", account_holder: "", account_last4: "", ...(e.bank || {}) },
  delay: { month: "", ...(e.delay || {}) },
  temp_stop: { alternate_method: "", resume_date: "", ...(e.temp_stop || {}) },
  stop: { reason: "", reason_text: "", move_out_date: "", alternate_method: "", ...(e.stop || {}) },
  renewal: { lease_end_date: "", ...(e.renewal || {}) },
  notes: e.notes || "",
});

/**
 * Create / edit one PAD Info entry. Two steps: pick a category, then fill the fields that
 * category needs. Line items are pre-filled from the unit's fee columns so the manager only
 * confirms the amount the bank should pull.
 */
const PadInfoModal = ({ isOpen, toggle, itemId, item, unitNo, buildingName, propertyName, tenantNames, incoming, existing, onSaved }) => {
  const [form, setForm] = useState(() => (existing ? fromExisting(existing) : emptyForm(item, tenantNames)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setForm(existing ? fromExisting(existing) : emptyForm(item, tenantNames));
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?._id]);

  const meta = form.category ? categoryMeta(form.category) : null;
  const fields = meta ? meta.fields : [];
  const showAmount = fields.includes("amount");
  const computedTotal = useMemo(() => sumLineItems(form.line_items), [form.line_items]);
  const effectiveAmount = form.amountTouched && form.amount !== "" ? Number(form.amount) : computedTotal;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSub = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));
  const setLine = (i, patch) =>
    setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => (idx === i ? { ...li, ...patch } : li)), amountTouched: false }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { key: "", label: "", amount: "" }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i), amountTouched: false }));

  const pickCategory = (value) => {
    const m = categoryMeta(value);
    const patch = { category: value };
    // A New PAD is usually for the incoming resident — the row may still show the departing one.
    if (value === "NEW" && !existing && incoming?.tenants && (!form.tenant_names || form.tenant_names === tenantNames)) patch.tenant_names = incoming.tenants;
    if (value !== "NEW" && !existing && form.tenant_names === incoming?.tenants) patch.tenant_names = tenantNames || "";
    // Sensible defaults per category.
    if (value === "DELAY" && form.effective_date && !form.delay.month) patch.delay = { month: monthOf(form.effective_date) };
    if (m.fields.includes("amount") && !form.line_items.length) patch.line_items = lineItemsFromItem(item);
    set(patch);
  };

  const validate = () => {
    if (!form.category) return "Pick what kind of PAD update this is.";
    if (!form.effective_date) return "The effective date is required.";
    if (form.category === "DELAY" && !(form.delay.month || monthOf(form.effective_date))) return "Which month is being delayed?";
    if (form.category === "TEMP_STOP" && !form.temp_stop.alternate_method) return "How is the resident paying meanwhile?";
    if (form.category === "STOP" && !form.stop.reason) return "Please pick a reason for stopping the PAD.";
    if (form.bank.account_last4 && !/^\d{4}$/.test(form.bank.account_last4)) return "Bank account last 4 must be exactly four digits.";
    if (showAmount && (effectiveAmount === null || isNaN(effectiveAmount) || effectiveAmount < 0)) return "The monthly amount must be a number.";
    return "";
  };

  const save = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      mastersheet_item_id: itemId,
      category: form.category,
      effective_date: form.effective_date,
      pull_day: form.category === "DELAY" ? Number(form.effective_date.slice(8, 10)) || 1 : Number(form.pull_day) || 1,
      line_items: showAmount ? form.line_items.filter((li) => li.key || li.label).map((li) => ({ key: li.key || li.label, label: li.label || li.key, amount: Number(li.amount) || 0 })) : [],
      amount: form.category === "STOP" ? null : showAmount ? effectiveAmount : null,
      notes: form.notes,
      tenant_names: (form.tenant_names || tenantNames || "").trim(),
    };
    if (fields.includes("bank")) payload.bank = form.bank;
    if (fields.includes("delay")) payload.delay = { month: form.delay.month || monthOf(form.effective_date) };
    if (fields.includes("temp_stop")) payload.temp_stop = form.temp_stop;
    if (fields.includes("stop")) payload.stop = form.stop;
    if (fields.includes("renewal")) payload.renewal = form.renewal;
    try {
      if (existing?._id) await updatePadEntry(existing._id, payload);
      else await createPadEntry(payload);
      toast.success(existing?._id ? "PAD info updated" : "PAD info recorded");
      onSaved && onSaved();
      toggle();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save PAD info";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const effectiveLabel =
    form.category === "DELAY" ? "Pull on (the delayed date)" :
    form.category === "TEMP_STOP" ? "First pull skipped" :
    form.category === "STOP" ? "PAD stops from" :
    form.category === "NEW" ? "First pull date" : "Effective from";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered contentClassName="pad-info-modal-content">
      <style>{`
        .pad-info-modal-content { border: none; border-radius: 16px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .pad-info-modal-content input::placeholder, .pad-info-modal-content textarea::placeholder { color: #9AA7B8; }
        .pad-info-modal-content select:focus, .pad-info-modal-content input:focus, .pad-info-modal-content textarea:focus { outline: none; }
        .pim-focusable:focus-within { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.14); }
        .pim-cat { border: 1px solid ${C.border}; border-radius: 10px; background: #fff; padding: 10px 12px; cursor: pointer; text-align: left;
          display: flex; gap: 10px; align-items: flex-start; transition: border-color .12s, box-shadow .12s, background .12s; font-family: inherit; }
        .pim-cat:hover { border-color: #B9C6D8; background: #FBFCFE; }
        .pim-cat.on { box-shadow: 0 0 0 2px rgba(26,111,181,0.18); border-color: ${C.primaryMd}; }
        .pim-cat .ic { width: 30px; height: 30px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; flex: none; }
        .pim-cat .t { font-size: 13.5px; font-weight: 700; color: ${C.text}; line-height: 1.2; }
        .pim-cat .b { font-size: 11.5px; color: ${C.muted}; margin-top: 2px; line-height: 1.35; }
        .pim-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 8px; }
        .pim-btn { border: none; cursor: pointer; font-size: 13.5px; font-weight: 600; border-radius: 8px; padding: 9px 18px; display: inline-flex; align-items: center; gap: 7px; font-family: inherit; }
        .pim-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .pim-btn-primary:hover:not(:disabled) { background: #083460; }
        .pim-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .pim-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .pim-btn-ghost:hover { background: #F1F5FA; }
        .pim-add { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; margin-top: 8px; border: 1px dashed ${C.inputBorder}; border-radius: 8px;
          background: #fff; cursor: pointer; font-size: 12px; font-weight: 600; color: ${C.primary}; font-family: inherit; }
        .pim-add:hover { background: #EBF3FC; border-color: ${C.primaryMd}; }
        .pim-line { display: grid; grid-template-columns: minmax(140px, 1.2fr) 130px 30px; gap: 8px; align-items: center; margin-top: 6px; }
        .pim-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .pim-iconbtn:hover { color: #991B1B; }
        .pim-select { border: none; outline: none; background: transparent; flex: 1; width: 100%; font-size: ${T.body}px; color: ${C.text}; height: 100%; font-family: inherit; }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ width: 36, height: 36, borderRadius: 10, background: "#EBF3FC", color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          <FeatherIcon icon="credit-card" size={18} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.title, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
            {existing?._id ? "Edit PAD info" : "Record PAD info"}
          </div>
          <div style={{ fontSize: T.hint, color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Unit {unitNo || "—"}
            {buildingName ? ` · ${buildingName}` : ""}
            {propertyName ? ` · ${propertyName}` : ""}
            {tenantNames ? ` · ${tenantNames}` : ""}
          </div>
        </div>
        <button type="button" className="pim-iconbtn" onClick={toggle} aria-label="Close">
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      <div style={styles.body}>
        {/* Step 1 — category */}
        <div style={{ ...styles.card, marginBottom: 12 }}>
          <div style={styles.cardHead}>
            <span style={styles.sectionTitle}>1. What kind of PAD update is this?</span>
          </div>
          <div style={styles.cardBody}>
            <div className="pim-grid">
              {PAD_CATEGORIES.map((c) => (
                <button key={c.value} type="button" className={`pim-cat${form.category === c.value ? " on" : ""}`} onClick={() => pickCategory(c.value)}>
                  <span className="ic" style={{ background: c.bg, color: c.color }}>
                    <FeatherIcon icon={c.icon} size={15} />
                  </span>
                  <span>
                    <span className="t">{c.label}</span>
                    <span className="b" style={{ display: "block" }}>{c.blurb}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step 2 — details */}
        {meta && (
          <div style={{ ...styles.card, marginBottom: 12 }}>
            <div style={styles.cardHead}>
              <span style={{ width: 24, height: 24, borderRadius: 6, background: meta.bg, color: meta.color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <FeatherIcon icon={meta.icon} size={13} />
              </span>
              <span style={styles.sectionTitle}>2. {meta.label} details</span>
            </div>
            <div style={styles.cardBody}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                {form.category === "NEW" && (
                  <Field label="Resident(s) this PAD is for" style={{ gridColumn: "1 / -1" }} hint={incoming?.tenants && form.tenant_names === incoming.tenants ? `Pre-filled from the signed lease starting ${incoming.start_date || "soon"}.` : undefined}>
                    <Box>
                      <FeatherIcon icon="user" size={14} color={C.muted} />
                      <input style={styles.bareInput} placeholder="Name(s) on the lease" value={form.tenant_names} onChange={(e) => set({ tenant_names: e.target.value })} />
                    </Box>
                  </Field>
                )}
                <Field label={effectiveLabel}>
                  <Box>
                    <FeatherIcon icon="calendar" size={14} color={C.muted} />
                    <input
                      type="date"
                      style={styles.bareInput}
                      value={form.effective_date}
                      onChange={(e) => {
                        const v = e.target.value;
                        set({ effective_date: v, ...(form.category === "DELAY" && !form.delay.month ? { delay: { month: monthOf(v) } } : {}) });
                      }}
                    />
                  </Box>
                </Field>

                {form.category !== "DELAY" && form.category !== "STOP" && (
                  <Field label="Regular pull day" hint="Day of the month the bank pulls (usually 1).">
                    <Box>
                      <input type="number" min={1} max={31} style={styles.bareInput} value={form.pull_day} onChange={(e) => set({ pull_day: e.target.value })} />
                    </Box>
                  </Field>
                )}

                {fields.includes("delay") && (
                  <Field label="Month being delayed">
                    <Box>
                      <input type="month" style={styles.bareInput} value={form.delay.month} onChange={(e) => setSub("delay", { month: e.target.value })} />
                    </Box>
                  </Field>
                )}

                {fields.includes("temp_stop") && (
                  <>
                    <Field label="Paying meanwhile by">
                      <Box>
                        <select className="pim-select" value={form.temp_stop.alternate_method} onChange={(e) => setSub("temp_stop", { alternate_method: e.target.value })}>
                          <option value="">Select…</option>
                          {ALTERNATE_METHODS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </Box>
                    </Field>
                    <Field label="PAD resumes on" hint="Leave blank if open-ended.">
                      <Box>
                        <input type="date" style={styles.bareInput} value={form.temp_stop.resume_date} onChange={(e) => setSub("temp_stop", { resume_date: e.target.value })} />
                      </Box>
                    </Field>
                  </>
                )}

                {fields.includes("stop") && (
                  <>
                    <Field label="Reason">
                      <Box>
                        <select className="pim-select" value={form.stop.reason} onChange={(e) => setSub("stop", { reason: e.target.value })}>
                          <option value="">Select…</option>
                          {STOP_REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </Box>
                    </Field>
                    <Field label="Move-out date">
                      <Box>
                        <input type="date" style={styles.bareInput} value={form.stop.move_out_date} onChange={(e) => setSub("stop", { move_out_date: e.target.value })} />
                      </Box>
                    </Field>
                    <Field label="Paying going forward (if any)">
                      <Box>
                        <select className="pim-select" value={form.stop.alternate_method} onChange={(e) => setSub("stop", { alternate_method: e.target.value })}>
                          <option value="">—</option>
                          {ALTERNATE_METHODS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </Box>
                    </Field>
                    <Field label="Reason details" style={{ gridColumn: "1 / -1" }}>
                      <Box>
                        <input style={styles.bareInput} placeholder="e.g. Moving to Ontario, bought house in Leduc…" value={form.stop.reason_text} onChange={(e) => setSub("stop", { reason_text: e.target.value })} />
                      </Box>
                    </Field>
                  </>
                )}

                {fields.includes("renewal") && (
                  <Field label="New lease end date">
                    <Box>
                      <input type="date" style={styles.bareInput} value={form.renewal.lease_end_date} onChange={(e) => setSub("renewal", { lease_end_date: e.target.value })} />
                    </Box>
                  </Field>
                )}

                {fields.includes("bank") && (
                  <>
                    <Field label="Bank / institution">
                      <Box>
                        <input style={styles.bareInput} placeholder="TD, RBC, Servus…" value={form.bank.institution} onChange={(e) => setSub("bank", { institution: e.target.value })} />
                      </Box>
                    </Field>
                    <Field label="Account holder">
                      <Box>
                        <input style={styles.bareInput} placeholder="Name on the account" value={form.bank.account_holder} onChange={(e) => setSub("bank", { account_holder: e.target.value })} />
                      </Box>
                    </Field>
                    <Field label="Account — last 4 digits" hint="Only the last four digits are stored. Keep the signed PAD form / void cheque in Documents.">
                      <Box>
                        <input
                          style={styles.bareInput}
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="1234"
                          value={form.bank.account_last4}
                          onChange={(e) => setSub("bank", { account_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        />
                      </Box>
                    </Field>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — amount */}
        {meta && showAmount && (
          <div style={{ ...styles.card, marginBottom: 12 }}>
            <div style={{ ...styles.cardHead, justifyContent: "space-between" }}>
              <span style={styles.sectionTitle}>3. Monthly amount the bank pulls</span>
              <span style={{ fontSize: T.body, fontWeight: 700, color: C.text }}>
                Total {money(effectiveAmount)}
                {form.amountTouched && form.amount !== "" && Number(form.amount) !== computedTotal ? (
                  <span style={{ ...styles.hint, marginLeft: 8 }}>(line items add up to {money(computedTotal)})</span>
                ) : null}
              </span>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.hint}>Pre-filled from the unit's fee columns. Adjust anything that differs on the PAD form.</div>
              {form.line_items.map((li, i) => (
                <div className="pim-line" key={i}>
                  <Box>
                    <select
                      className="pim-select"
                      value={LINE_ITEM_KEYS.some((k) => k.key === li.key) ? li.key : li.key ? "__custom" : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__custom") setLine(i, { key: li.key || "custom", label: li.label || "" });
                        else {
                          const k = LINE_ITEM_KEYS.find((x) => x.key === v);
                          setLine(i, { key: v, label: k ? k.label : "" });
                        }
                      }}
                    >
                      <option value="">Item…</option>
                      {LINE_ITEM_KEYS.map((k) => (
                        <option key={k.key} value={k.key}>{k.label}</option>
                      ))}
                      <option value="__custom">Other (type a name)</option>
                    </select>
                    {(!LINE_ITEM_KEYS.some((k) => k.key === li.key) && li.key) ? (
                      <input style={{ ...styles.bareInput, flex: 1.2 }} placeholder="Name" value={li.label} onChange={(e) => setLine(i, { label: e.target.value, key: e.target.value ? e.target.value.replace(/\s+/g, "_").toLowerCase() : "custom" })} />
                    ) : null}
                  </Box>
                  <Box>
                    <span style={{ color: C.muted }}>$</span>
                    <input type="number" step="0.01" min="0" style={{ ...styles.bareInput, textAlign: "right" }} value={li.amount} onChange={(e) => setLine(i, { amount: e.target.value })} />
                  </Box>
                  <button type="button" className="pim-iconbtn" onClick={() => removeLine(i)} aria-label="Remove line">
                    <FeatherIcon icon="trash-2" size={14} />
                  </button>
                </div>
              ))}
              <button type="button" className="pim-add" onClick={addLine}>
                <FeatherIcon icon="plus" size={12} /> Add line
              </button>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.2fr) 130px 30px", gap: 8, alignItems: "center", marginTop: 12 }}>
                <span style={{ ...styles.label, marginBottom: 0 }}>Or override the total</span>
                <Box>
                  <span style={{ color: C.muted }}>$</span>
                  <input
                    type="number" step="0.01" min="0"
                    style={{ ...styles.bareInput, textAlign: "right" }}
                    placeholder={String(computedTotal)}
                    value={form.amountTouched ? form.amount : ""}
                    onChange={(e) => set({ amount: e.target.value, amountTouched: e.target.value !== "" })}
                  />
                </Box>
                <span />
              </div>
            </div>
          </div>
        )}

        {meta && (
          <div style={{ ...styles.card, marginBottom: 4 }}>
            <div style={styles.cardHead}>
              <span style={styles.sectionTitle}>{showAmount ? "4." : "3."} Notes for accounting</span>
            </div>
            <div style={styles.cardBody}>
              <div className="pim-focusable" style={{ border: `1px solid ${C.inputBorder}`, borderRadius: 8, background: "#fff", padding: "8px 10px" }}>
                <textarea
                  rows={3}
                  style={{ ...styles.bareInput, height: "auto", resize: "vertical", width: "100%" }}
                  placeholder="Anything accounting should know — e.g. “Sending e-transfer for Jan”, “SD received”, “Joel's suite”…"
                  value={form.notes}
                  onChange={(e) => set({ notes: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 20px", borderTop: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ ...styles.hint, color: error ? "#A32D2D" : C.muted, fontWeight: error ? 600 : 500 }}>
          {error || (meta ? meta.blurb : "Start by picking a category above.")}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="pim-btn pim-btn-ghost" onClick={toggle} disabled={saving}>Cancel</button>
          <button type="button" className="pim-btn pim-btn-primary" onClick={save} disabled={saving || !meta}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={14} />}
            {existing?._id ? "Save changes" : "Record PAD info"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PadInfoModal;
